/**
 * T-046: fleet QR metrics + source breakdown + missing-QR link.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import type { PocketQrFleet, PocketQrBreakdown } from '@/types/pocketSettings';

interface Props {
  fleet: PocketQrFleet;
  breakdown: PocketQrBreakdown;
}

function pct(part: number, total: number): string {
  if (!total) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

export function QrFleetMetricsCard({ fleet, breakdown }: Props) {
  const totalActive = fleet.units_with_active_qr;
  const missing = fleet.units_without_active_qr;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Fleet QR coverage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <Tile label="Total units" value={String(fleet.total_units)} />
          <Tile
            label="With active QR"
            value={String(totalActive)}
            sub={pct(totalActive, fleet.total_units)}
          />
          <Tile
            label="Missing QR"
            value={String(missing)}
            sub={
              missing > 0 ? (
                <Link
                  to="/settings/pocket?tab=fleet&filter=missing_qr"
                  className="text-xs underline text-foreground"
                >
                  Show units →
                </Link>
              ) : (
                <span className="text-xs text-muted-foreground">all good</span>
              )
            }
            tone={missing > 0 ? 'warn' : 'ok'}
          />
        </div>

        <div className="text-sm">
          <div className="text-muted-foreground mb-2">Active QR source breakdown</div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              Generated: <span className="font-semibold ml-1">{breakdown.generated_v1}</span>
            </Badge>
            <Badge variant="secondary">
              Uploaded: <span className="font-semibold ml-1">{breakdown.uploaded}</span>
            </Badge>
            {breakdown.uploaded_arbitrary > 0 ? (
              <Badge variant="outline" className="border-destructive/40 text-destructive">
                Override: <span className="font-semibold ml-1">{breakdown.uploaded_arbitrary}</span>
              </Badge>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className="rounded-md border border-border/50 p-4 bg-card">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={
          'text-2xl font-semibold tabular-nums mt-1 ' +
          (tone === 'warn' ? 'text-destructive' : '')
        }
      >
        {value}
      </div>
      {sub ? <div className="mt-1">{sub}</div> : null}
    </div>
  );
}
